/**
 * @typedef {import('../index.js').Store<State, Getters>} Store
 * @typedef {import('../booking').QueryOptions} QueryOptions
 */
/**
 * @template T
 * @typedef {import('../index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('../index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {Object} Cart
 * @property {string} id - Cart id
 * @property {Array<CartLine>} lines - Array of cart lines
 * @property {CartTotals} totals - Cart totals
 * @property {string} currency - Currency selected
 * @property {string} hotelCode - Hotel code
 * @property {string} iataNumber - Travel agent number
 * @property {string} groupCode - Code for group discount
 * @property {string} promotionCode - Code for promotional discount
 */
/**
 * @typedef {Object} CartLine
 * @property {string} [id] - Cart line id
 * @property {string} [hotelCode] - Hotel code
 * @property {string} [iataNumber] - Travel agent number
 * @property {string} [groupCode] - Code for group discount
 * @property {string} [promotionCode] - Code for promotional discount
 * @property {string} ratePlanCode - Rate code of this reservation
 * @property {string} roomTypeCode - Room code of this reservation
 * @property {string} bedTypeCode - Bed code of this reservation
 * @property {number} price - Price without taxes
 * @property {number} amount - Number of units
 * @property {number} taxes - Taxes
 * @property {number} fee - Fees
 * @property {number} total - Total price
 * @property {number} adultGuestCount - Number of adult guests
 * @property {number} childGuestCount - Number of child guests
 * @property {string} startDate - Start date in dayFormat
 * @property {string} endDate - End date in dayFormat
 * @property {Array<NightlyRate>} nightlyRates - Rate details by night
 * @property {Array<AddOn>} addOns - Addons packages (not in use at this moment)
 * @property {Array<Enhancement>} enhancements - Fans of MO Enhancements
 * @property {Array<TaxesFees>} taxesFees - Detailed list of taxes and fees
 * @property {SitecoreContent} sitecoreContent - Titles and labels from sitecore
 */
/**
 * @typedef {Object} CartTotals
 * @property {number} rooms - Number of rooms
 * @property {number} tax - Taxes amount
 * @property {number} fees - Fees amount
 * @property {number} packages - Packages amount
 * @property {number} price - Rooms amount
 * @property {number} total - Total amount
 */
/**
 * @typedef {Object} NightlyRate
 * @property {string} date - Date of the night
 * @property {number} price - Price for the night
 * @property {number} tax - Tax for the night
 * @property {number} fee - Fees for the night
 */
/**
 * @typedef {Object} RateAllowed
 * @property {string} hotelCode - SynXis Property Code
 * @property {string} rateCode - SynXis Rate Code
 */
/**
 * @typedef {Object} Enhancement
 * @property {string} sitecoreId - Sitecore ID of the enhancement
 * @property {string} synXisId - SynXis ID of the enhancement
 * @property {string} name - Name of the enhancement
 * @property {string} description - Description of the enhancement
 * @property {string} fanLevel - Fan level required for this enhancement
 * @property {boolean} [allRatesAllowed=true] - If true, the enhancement is allowed for all rates
 * @property {Array<RateAllowed>} [ratesAllowed] - If allRatesAllowed is false, the enhancement
 * should only be available for the rates in this list
 */
/**
 * @typedef {Object} SitecoreContent
 * @property {ContentItem} bed
 * @property {ContentItem} rate
 * @property {ContentItem} room
 */
/**
 * @typedef {Object} ContentItem
 * @property {string} title
 * @property {string} description
 * @property {string} image
 */
/**
 * @typedef {Object} TaxesFees
 * @property {number} price
 * @property {number} amount
 * @property {string} description
 * @property {string} code
 * @property {string} effectiveDate
 * @property {string} expireDate
 */
/**
 * @typedef {Object} InputMethods
 * @property {function(Enhancement): string} summary - Returns details summary as a string
 * @property {function(Enhancement): string} description - Returns enhancement description as a string
 * @property {function(Enhancement, CartLine, string): Promise} [onAdd] - Execute custom action before adding enhancement to cart
 * @property {function(Enhancement, CartLine, string): Promise} [onRemove] - Execute custom action before removing enhancement from cart
 */
/**
 * @typedef {Object} Rate
 * @property {string} synXisID - SynXis ID of the rate
 * @property {number} total - Total price
 * @property {number} taxes - Total taxes
 * @property {number} fees - Total fees
 */
/**
 * [TODO] - When we decide to implement AddOn's update this docs. // TODO: update docs for add-ons
 * @typedef {Object} AddOn
 * @property {string} id - Id of the package
 * @property {number} price - Price for the night
 * @property {number} total - Price for the night
 */
import axios from 'axios'
import { logger } from '@plugins/logger'
import { formatDateZone } from '@utils/date-helpers'
import { getApiUrl } from '@utils/api-helpers'
import { ENHANCEMENT_USER_INPUTS } from '@utils/constants'
/**
 * Input types
 */
import preferredTime from './enhancements/preferred-time'
import celebratoryTreat from './enhancements/celebratory-treat'
/**
 * @module Store:Cart
 * @description Handles all cart functionality
 */
// Input types mapping
const userInputMap = {
  [ENHANCEMENT_USER_INPUTS.EARLY_CHECK_IN]: preferredTime,
  [ENHANCEMENT_USER_INPUTS.LATE_CHECK_OUT]: preferredTime,
  [ENHANCEMENT_USER_INPUTS.CELEBRATORY_TREAT]: celebratoryTreat,
  [ENHANCEMENT_USER_INPUTS.NO_INPUT]: null
}

/**
 * Check if there are expecific methods for this user input
 * @param {string} userInput
 * @returns {null|InputMethods}
 */
const getUserInputMethods = (userInput) => {
  const methods = userInputMap[userInput]
  if (!methods) {
    return null
  }
  return methods
}

/**
 * Merge parameters from room added with
 * booking query to create a cart line request.
 * @param {CartLine} item
 * @param {RootGetters} getters
 * @returns {CartLine}
 */
const getParams = (item, getters, rootState) => {
  const {
    ratePlanCode,
    bedTypeCode,
    roomTypeCode,
    nightlyRates,
    sitecoreContent,
    total,
    fees,
    taxes,
    taxesFees,
    restrictionPolicy,
    upgradeRoom,
    upgradeSelected,
    upgradePriceDifferencePerNight,
    previousCartItem,
    previousRoomUpgradeInfo,
    previousRoomUpgradeImage,
    enhancements,
    confirmationNumber,
    guaranteeCode,
    bedTypeDescription,
  } = item
  /** @type {QueryOptions} */
  const {
    hotelCode,
    roomQuantity,
    stayDateStart,
    stayDateEnd,
    adultGuestCount,
    childGuestCount,
    iataNumber,
    groupCode,
    promotionCode
  } = getters['booking/getQuery']
  const dateTimeFormat = getters['booking/dateTimeFormat']
  const hotelData = getters['hotelData/getHotelById'](hotelCode)
  const checkInTime = hotelData?.checkIn ?? '12:00'
  const checkOutTime = hotelData?.checkOut ?? '14:00'

  const { isBookingEditMode } = rootState.modifyReservation
  const reservationConfirmationNumber = isBookingEditMode && confirmationNumber ? confirmationNumber : null

  return {
    hotelCode,
    roomTypeCode,
    bedTypeCode,
    ratePlanCode,
    roomQuantity,
    startDate: formatDateZone(`${stayDateStart} ${checkInTime}`, dateTimeFormat),
    endDate: formatDateZone(`${stayDateEnd} ${checkOutTime}`, dateTimeFormat),
    adultGuestCount,
    childGuestCount,
    iataNumber,
    groupCode,
    promotionCode,
    nightlyRates,
    sitecoreContent,
    total,
    fee: fees,
    taxes,
    taxesFees,
    restrictionPolicy,
    upgradeRoom,
    upgradeSelected,
    upgradePriceDifferencePerNight,
    previousCartItem,
    previousRoomUpgradeInfo,
    previousRoomUpgradeImage,
    enhancements,
    confirmationNumber: reservationConfirmationNumber,
    guaranteeCode,
    bedTypeDescription,
  }
}

export default {
  namespaced: true,
  /** Cart State
   * @typedef {Object} State
   * @property {Cart} cart - User cart
   * @property {Array<Enhancement>} roomEnhancements - Room enhancements data from sitecore
   * @property {Array<Enhancement>} validEnhancements - Enhancements valid by rate / user level
   * @property {Array<AddOn>} roomAddOns - Room add-ons data from sitecore
   * @property {Array<AddOn>} validAddOns - Add-ons valid grouped by synXisID
   * @property {boolean} cartPending - Api calls pending
   */
  /** @type {State} */
  state: {
    cart: null,
    roomEnhancements: [],
    roomAddOns: [],
    airportTransferAddOns: [],
    validEnhancements: [],
    validAddOns: [],
    cartPending: false,
    showRoomUpgradeButton: false,
    showMultiBookingSidebar: false,
    multiBookingSidebarData: null,
    resolveMultiBookingSidebar: null,
    roomUpgradeSelected: false,
    roomUpgradeLoading: false,
    roomUpgradesOptions: [],
    roomUpgradeAvailable: null,
    roomUpgradeTitle: null,
    roomUpgradeTotal: 0,
    roomUpgradePriceDifference: null,
    previousCartItem: {},
    previousRoomUpgradeInfo: {},
    previousRoomUpgradeImage: null,
    enhancementsMultiBookingSelected: false,
    enhancementsMultiBookingAvailable: false,
    isMultiRoomBookingEnabled: false,
  },
  /** Cart Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<Array<CartLine>>} itemsInCart - [Rooms]{@link CartLine} added to the [cart]{@link Cart}
   * @property {GetterFunction<Array<string>>} bedsIds - Bed id's in the [cart]{@link Cart}
   * @property {GetterFunction<boolean>} allRoomsAdded - True if all rooms from the query are added to the [cart]{@link Cart}
   * @property {GetterFunction<Array<Enhancement>>} enhancementsInRoom - [Enhancements]{@link Enhancement} added in the [current room]${@link CartLine}
   * @property {GetterFunction<function(number): Array<Enhancement>>} getEnhancementsInRoom - [Enhancements]{@link Enhancement} added in the [current room]${@link CartLine}
   * @property {GetterFunction<function(CartLine): Array<Enhancement>>} getEnhancements - Get [enhancements]{@link Enhancement} with user input data and sitecore data
   * @property {GetterFunction<number>} cartTotal - Total price in [cart]{@link Cart}
   * @property {GetterFunction<boolean>} getShowMultiBookingSidebar - True if multi-room is available in the [cart]{@link Cart}
   */
  /** @type {Getters} */
  getters: {
    itemsInCart(state) {
      return state.cart && state.cart.lines ? state.cart.lines : []
    },
    cartLineIds(state, getters) {
      return getters.itemsInCart.map((i) => i.id)
    },
    bedsIds(state, getters) {
      return getters.itemsInCart.map((i) => i.bedTypeCode)
    },
    allRoomsAdded(state, getters, rootState) {
      const roomsToSelect = parseInt(rootState.booking.query.roomQuantity)
      return roomsToSelect === getters.itemsInCart.length
    },
    enhancementsInRoom(state, getters, rootState, rootGetters) {
      const selectedRoom = rootGetters['booking/currentRoom']
      return getters.getEnhancements(selectedRoom)
    },
    addOnsInRoom(state, getters, rootState, rootGetters) {
      const selectedRoom = rootGetters['booking/currentRoom']
      return getters.getAddOns(selectedRoom, rootGetters['booking/getCurrentRoomId'])
    },
    enhancementsInRoomWithEmptySummary(state, getters) {
      return getters.enhancementsInRoom.some((enhancement) => enhancement?.summary === '')
    },
    addOnsInRoomWithEmptyData(state, getters) {
      return getters.itemsInCart.some((item, index) => {
        const addOnsInRoom = getters.getAddOns(item, index)
        return addOnsInRoom.some((addOn) => {
          if (Object.keys(addOn.data).length !== 0) {
            return false
          }
          return addOn.airportAddOn || (addOn.addOn?.availableDates.length > 0 && addOn.addOn?.servicePricingType !== 'per-night')
        })
      })
    },
    getEnhancementsInRoom: (state, getters) => (roomIndex) => {
      const selectedRoom = state.cart.lines[roomIndex]
      return getters.getEnhancements(selectedRoom)
    },
    getAddOnsInRoom: (state, getters) => (roomIndex) => {
      const selectedRoom = state.cart.lines[roomIndex]
      return getters.getAddOns(selectedRoom, roomIndex)
    },
    addOnsInCart: (state, getters) => getters.itemsInCart.reduce((acc, item) => {
      if (item.addOns) {
        acc.push(...item.addOns)
      }
      return acc
    }, []),
    getEnhancements: (state) => (selectedRoom) => (selectedRoom && selectedRoom.enhancements ? selectedRoom.enhancements.map((e) => {
      const enhancement = state.roomEnhancements.find((en) => en.sitecoreId === e.sitecoreId)
      const clone = {
        ...e,
        enhancement
      }
      const methods = getUserInputMethods(enhancement.userInput)
      if (methods) {
        clone.summary = e.data && methods.summary ? methods?.summary(e) : ''
        clone.summaryShort = e.data && methods.summaryShort ? methods?.summaryShort(e) : ''
      }
      return clone
    }) : []),
    getAddOns: (state) => (selectedRoom, roomIndex) =>
      // TODO: check with Pritam the expected data inside data obj
      (selectedRoom?.addOns ? selectedRoom?.addOns?.map((e) => {
        const addOn = state.validAddOns[roomIndex]?.find((en) => en.sitecoreId === e.sitecoreId)
        const clone = {
          ...e,
          addOn
        }
        return clone
      }) : []),
    cartTotal(state) {
      return state.cart?.totals?.total ?? 0
    },
    getRoomUpgradesOptions(state) {
      return state.roomUpgradesOptions ? state.roomUpgradesOptions : []
    },
    getRoomUpgradeAvailable(state) {
      return state.roomUpgradeAvailable ? state.roomUpgradeAvailable : null
    },
    getRoomUpgradeAvailableInfo(state, getters) {
      return getters.getRoomUpgradesOptions.find((upgradeOpt) => getters.getRoomUpgradeAvailable.bedType.find((bed) => bed.synXisID === upgradeOpt.bedTypeSynXisId))
    },
    getRoomUpgradePriceDifferencePerNight(state, getters, rootState, rootGetters) {
      if (state.roomUpgradeSelected) {
        return getters.itemsInCart[0].upgradePriceDifferencePerNight
      }
      if (state.roomUpgradeAvailable) {
        const totalPerNightCurrentRoom = state.cart.lines[0] ? state.cart.lines[0].nightlyRates.map((n) => Number(n.price)).reduce((a, b) => a + b) : 0
        const totalPerNightUpgradeRoom = state.roomUpgradeAvailable ? state.roomUpgradeAvailable.rates[0].nightlyRates.map((n) => Number(n.price)).reduce((a, b) => a + b) : 0

        const avgPerNightCurrentRoom = totalPerNightCurrentRoom / state.cart.lines[0].nightlyRates.length
        const avgPerNightUpgradeRoom = totalPerNightUpgradeRoom / state.roomUpgradeAvailable.rates[0].nightlyRates.length

        const priceDifferencePerNight = avgPerNightUpgradeRoom - avgPerNightCurrentRoom

        return priceDifferencePerNight
      }
      return 0
    },
    getShowMultiBookingSidebar(state) {
      return state.showMultiBookingSidebar ? state.showMultiBookingSidebar : null
    },
    getResolveMultiBookingSidebar(state) {
      return state.resolveMultiBookingSidebar ? state.resolveMultiBookingSidebar : null
    },
    isMultiRoomBookingEnabled(state) {
      return state.isMultiRoomBookingEnabled ? state.isMultiRoomBookingEnabled : null
    },
    multiBookingSidebarData(state) {
      return state.multiBookingSidebarData ? state.multiBookingSidebarData : null
    },
    getValidEnhancementsByRoomId: (state) => (roomId) => (state.validEnhancements && state.validEnhancements[roomId] ? state.validEnhancements[roomId] : []),
    getValidAddOnsByRoomId: (state) => (roomId) => state?.validAddOns[roomId] ?? [],
    getAddOnQuantity: (state, getters) => (addOn) => {
      if (addOn.quantity) {
        return Number(addOn.quantity)
      }
      const childQuantity = addOn.excludeKidsFromPriceCalculations ? 0 : Number(addOn.childQuantity)
      return Number(addOn.adultQuantity) + childQuantity
    },
    getTotalTaxPerAddOn: (state, getters) => (addOn) => {
      let totalAddOnTax = 0
      Object.entries(addOn.addOnTax).forEach(([date, taxes]) => {
        taxes.forEach((tax) => {
          const price = parseFloat(tax.price)
          totalAddOnTax += price
        })
      })
      totalAddOnTax *= getters.getAddOnQuantity(addOn)
      return Number(totalAddOnTax)
    },
    addOnsTotalTaxes: (state, getters) => {
      let total = 0
      if (!Array.isArray(getters.addOnsInCart)) {
        return 0
      }
      getters.addOnsInCart.forEach((addOn) => { total += getters.getTotalTaxPerAddOn(addOn) })
      return total
    }
  },
  /** Mutations */
  mutations: {
    UPDATE_CART(/** @type {State} */
      state, /** @type {Cart} */
      cart
    ) {
      state.cart = cart
    },
    UPDATE_ROOM_UPGRADES_AVAILABLE(/** @type {State} */
      state, /** @type {Cart} */
      roomUpgradesOptions
    ) {
      state.roomUpgradesOptions = roomUpgradesOptions
    },
    CART_REQUEST_PENDING(/** @type {State} */
      state
    ) {
      state.cartPending = true
    },
    CART_REQUEST_NOT_PENDING(/** @type {State} */
      state
    ) {
      state.cartPending = false
    },
    CLEAN_CART(/** @type {State} */
      state
    ) {
      state.cart = null
    },
    REGISTER_ENHANCEMENTS(/** @type {State} */
      state, /** @type {Array<Enhancement>} */
      enhancements
    ) {
      state.roomEnhancements = enhancements
    },
    REGISTER_ADD_ONS(/** @type {State} */
      state, /** @type {Array<AddOn>} */
      addOns
    ) {
      state.roomAddOns = addOns
    },
    REGISTER_AIRPORT_TRANSFER_ADD_ONS(/** @type {State} */
      state, /** @type {Array<AddOn>} */
      airportTransferAddOns
    ) {
      state.airportTransferAddOns = airportTransferAddOns
    },
    ENHANCEMENT_SELECTED_FOR_MULTIBOOKING(/** @type {State} */
      state, /** @type {Array<Enhancement>} */
      status
    ) {
      state.enhancementsMultiBookingSelected = status
    },
    ENHANCEMENT_MULTIBOOKING_AVAILABLE(/** @type {State} */
      state, /** @type {Array<Enhancement>} */
      status
    ) {
      state.enhancementsMultiBookingAvailable = status
    },
    UPDATE_VALID_ENHANCEMENTS(/** @type {State} */
      state, /** @type {Array<Enhancement>} */
      enhancements
    ) {
      state.validEnhancements = enhancements
    },
    UPDATE_VALID_ADD_ONS(/** @type {State} */
      state, /** @type {Array<Enhancement>} */
      addOns
    ) {
      state.validAddOns = addOns
    },
    RESET_VALID_ENHANCEMENTS(/** @type {State} */
      state, /** @type {Array<Enhancement>} */
    ) {
      state.validEnhancements = []
    },
    UPDATE_ROOM_UPGRADE_TITLE(/** @type {State} */
      state,
      roomUpgradeTitle
    ) {
      state.roomUpgradeTitle = roomUpgradeTitle
    },
    UPDATE_ROOM_UPGRADE_TOTAL(/** @type {State} */
      state,
      roomUpgradeTotal
    ) {
      state.roomUpgradeTotal = roomUpgradeTotal
    },
    UPDATE_ROOM_UPGRADE_PRICE_DIFFERENCE(/** @type {State} */
      state,
      roomUpgradePriceDifference
    ) {
      state.roomUpgradePriceDifference = roomUpgradePriceDifference
    },
    UPDATE_ROOM_UPGRADE_AVAILABLE(/** @type {State} */
      state,
      roomUpgrade
    ) {
      state.roomUpgradeAvailable = roomUpgrade
    },
    UPDATE_SHOW_ROOM_UPGRADE_BUTTON(/** @type {State} */
      state,
      showRoomUpgradeButton
    ) {
      state.showRoomUpgradeButton = showRoomUpgradeButton
    },
    UPDATE_ROOM_UPGRADE_SELECTED(/** @type {State} */
      state,
      roomUpgradeSelected
    ) {
      state.roomUpgradeSelected = roomUpgradeSelected
    },
    LOADING_ROOM_UPGRADES(/** @type {State} */
      state,
      loading
    ) {
      state.roomUpgradeLoading = loading
    },
    UPDATE_PREVIOUS_CART_ITEM(/** @type {State} */
      state,
      previousCartItem
    ) {
      state.previousCartItem = previousCartItem
    },
    UPDATE_PREVIOUS_ROOM_UPGRADE_INFO(/** @type {State} */
      state,
      previousRoomUpgradeInfo
    ) {
      state.previousRoomUpgradeInfo = previousRoomUpgradeInfo
    },
    UPDATE_PREVIOUS_ROOM_UPGRADE_IMAGE(/** @type {State} */
      state,
      previousRoomUpgradeImage
    ) {
      state.previousRoomUpgradeImage = previousRoomUpgradeImage
    },
    UPDATE_SHOW_MULTIBOOKING_SIDEBAR(/** @type {State} */
      state,
      showMultiBookingSidebar
    ) {
      state.showMultiBookingSidebar = showMultiBookingSidebar
    },
    RESOLVE_MULTIBOOKING_SIDEBAR(/** @type {State} */
      state,
      resolveMultiBookingSidebar
    ) {
      state.resolveMultiBookingSidebar = resolveMultiBookingSidebar
    },
    UPDATE_MULTIBOOKING_SIDEBAR_DATA(/** @type {State} */
      state,
      multiBookingSidebarData
    ) {
      state.multiBookingSidebarData = multiBookingSidebarData
    },
  },
  /** Auth Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<Array<Enhancement>,void>} registerEnhancements - Register existing {@link Enhancement}s from sitecore.
   * @property {ActionFunction<Array<AddOn>,void>} registerAddOns - Register existing {@link AddOn}s from sitecore.
   * @property {ActionFunction<undefined,void>} validateEnhancements - Validate witch of the registered {@link Enhancement}s are available for this user and rate.
   * @property {ActionFunction<undefined,void>} getCart - Get {@link Cart} data from server.
   * @property {ActionFunction<undefined,void>} resetCart - Reset {@link Cart} from session
   * @property {ActionFunction<CartLine,void>} addToCart - Add {@link Room} to {@link Cart}.
   * @property {ActionFunction<CartLine,void>} updateCartLine - Update current {@link CartLine}.
   * @property {ActionFunction<number,void>} deleteFromCart - Delete cart line from {@link CartLine}.
   * @property {ActionFunction<{enhancement: Enhancement},void>} addEnhancementToRoom - Add {@link Enhancement} to current {@link Room}.
   * @property {ActionFunction<{enhancementId: string},void>} removeEnhancementFromRoom - Remove an {@link Enhancement} from the [current]{@link /docs/store/booking#State} {@link Room}.
   */
  /** @type {Actions} */
  actions: {
    registerEnhancements({
      commit
    }, enhancements) {
      commit('REGISTER_ENHANCEMENTS', enhancements)
    },
    registerAddOns({
      commit
    }, { addOns = [], airportTransferAddOns = [] }) {
      commit('REGISTER_ADD_ONS', addOns)
      commit('REGISTER_AIRPORT_TRANSFER_ADD_ONS', airportTransferAddOns)
    },
    resetValidAddOnsAndPrivileges({ commit }) {
      commit('UPDATE_VALID_ENHANCEMENTS', [])
      commit('UPDATE_VALID_ADD_ONS', [])
    },
    selectEnhancementsForMultiBooking({
      commit
    }, status) {
      commit('ENHANCEMENT_SELECTED_FOR_MULTIBOOKING', status)
    },
    enhancementsMultiBookingAvailability({
      commit,
      getters,
      rootGetters,
    }) {
      const rooms = getters.itemsInCart
      const hasIataNumber = rootGetters['booking/getQuery'].iataNumber !== null
      const currentRoomId = rootGetters['booking/getCurrentRoomId']

      if (rooms.length === 1 || hasIataNumber || currentRoomId > 0) {
        commit('ENHANCEMENT_MULTIBOOKING_AVAILABLE', false)
        return
      }

      const firstRoomTypeCode = rooms[0].roomTypeCode
      const firstRatePlanCode = rooms[0].ratePlanCode

      const isVisible = rooms.every((room) => room.roomTypeCode === firstRoomTypeCode && room.ratePlanCode === firstRatePlanCode)
      commit('ENHANCEMENT_MULTIBOOKING_AVAILABLE', isVisible)
    },
    async validateEnhancements(
      {
        commit, state, getters, rootState, rootGetters
      }
    ) {
      commit('CART_REQUEST_PENDING')
      try {
        const requests = getters.itemsInCart.map((item) => ({
          HotelCode: rootGetters['booking/getQuery']?.hotelCode,
          AdultGuestCount: item?.adultGuestCount,
          ChildGuestCount: item?.childGuestCount,
          Start: item.startDate,
          End: item.endDate,
          RateCode: item.ratePlanCode
        }))

        const currentFanLevel = rootGetters['auth/fanLevel']?.replace('+','')
        const filteredComplimentaryPrivileges = []
        let filteredAddOns = []
        const filteredAirpotTransferAddOns = []
        const privilegesFromSitecoreMap = new Map(state.roomEnhancements.map((item) => [item.synXisId, item]))
        const addOnsFromSitecoreMap = new Map(state.roomAddOns.map((item) => [item.synXisId, item]))
        const airportTransferAddOnsFromSitecore = JSON.parse(JSON.stringify(state.airportTransferAddOns))

        const validateEnhancementsResponse = await Promise.all(requests.map(async (request, index) => {
          try {
            const { data } = await axios.post(getApiUrl('/api/v1/booking/validate-enhancements'), request)
            return {
              roomIndex: index,
              enhancements: data?.enhancements || []
            }
          } catch (error) {
            logger.error(`Error fetching enhancements for item at index ${index}:`, error)
            return {
              roomIndex: index, enhancements: [], hotelCode: request.HotelCode, rateCode: request.RateCode
            }
          }
        }))

        validateEnhancementsResponse.forEach((response) => {
          if (!filteredComplimentaryPrivileges[response.roomIndex]) {
            filteredComplimentaryPrivileges[response.roomIndex] = []
          }
          if (!filteredAddOns[response.roomIndex]) {
            filteredAddOns[response.roomIndex] = []
          }
          if (!filteredAirpotTransferAddOns[response.roomIndex]) {
            filteredAirpotTransferAddOns[response.roomIndex] = []
          }

          response.enhancements.forEach((enhancement) => {
            // Check if the enhancement exists in the sitecore data
            const sitecoreEnhancement = privilegesFromSitecoreMap.get(enhancement.code)

            // Process complimentary privileges with rate filtering
            if (sitecoreEnhancement) {
              const cartItem = getters.itemsInCart[response.roomIndex]

              // If allRatesAllowed is false, check if the current rate is in the ratesAllowed list
              if (sitecoreEnhancement.allRatesAllowed === false
                  && Array.isArray(sitecoreEnhancement.ratesAllowed)) {
                const isRateAllowed = sitecoreEnhancement.ratesAllowed.some(
                  (rate) => rate.RateCode === cartItem.ratePlanCode
                )

                // Skip this enhancement if the rate is not allowed
                if (!isRateAllowed) {
                  return
                }
              }

              // Process complimentary privileges
              if (sitecoreEnhancement.fanLevel === currentFanLevel) {
                filteredComplimentaryPrivileges[response.roomIndex].push({ ...enhancement })
              }
            }

            // Process add-ons without rate filtering
            if (addOnsFromSitecoreMap.has(enhancement.code)) {
              const addOnsDetails = addOnsFromSitecoreMap.get(enhancement.code)
              let existingAddOn = filteredAddOns[response.roomIndex].find((addOn) => addOn.synXisId === enhancement.code)

              if (!existingAddOn) {
                existingAddOn = {
                  ...addOnsDetails,
                  code: enhancement.code,
                  adultQuantity: enhancement.adultQuantity,
                  childQuantity: enhancement.childQuantity,
                  price: [],
                  servicePricingType: enhancement.servicePricingType,
                  availableDates: [],
                  airportAddOn: false
                }
                filteredAddOns[response.roomIndex].push(existingAddOn)
              }
              existingAddOn.price.push(...enhancement.price)

              if (!!enhancement.startTime && !!enhancement.endTime) {
                existingAddOn.availableDates.push({
                  startTime: enhancement.startTime,
                  endTime: enhancement.endTime,
                  price: enhancement.price
                })
              }
            }

            // Process airport transfer add-ons without rate filtering
            if (airportTransferAddOnsFromSitecore) {
              airportTransferAddOnsFromSitecore.forEach((airportTransferAddOn) => {
                airportTransferAddOn.vehicleDetails.forEach((vehicleDetail) => {
                  if (vehicleDetail.synxisId === enhancement.code) {
                    // Check if an entry already exists for this room index
                    let existingAirportTransfer = filteredAirpotTransferAddOns[response.roomIndex].find((item) => item.sitecoreId === airportTransferAddOn.sitecoreId)
                    if (!existingAirportTransfer) {
                      existingAirportTransfer = {
                        ...airportTransferAddOn,
                        vehicleDetails: [],
                        airportAddOn: true
                      }
                      filteredAirpotTransferAddOns[response.roomIndex].push(existingAirportTransfer)
                    }
                    existingAirportTransfer.vehicleDetails.push({
                      ...vehicleDetail, price: enhancement.price, quantity: enhancement.quantity, servicePricingType: enhancement.servicePricingType
                    })

                    if (!existingAirportTransfer.servicePricingType) {
                      existingAirportTransfer.servicePricingType = enhancement.servicePricingType
                    }
                  }
                })
              })
            }
          })
        })

        // Merging filteredAirpotTransferAddOns into filteredAddOns so we can easily use this in FE components
        filteredAddOns = filteredAddOns.map((addOn, index) => (filteredAirpotTransferAddOns[index]
          ? [...filteredAirpotTransferAddOns[index], ...addOn]
          : addOn))

        commit('UPDATE_VALID_ENHANCEMENTS', filteredComplimentaryPrivileges)
        commit('UPDATE_VALID_ADD_ONS', filteredAddOns)
      } catch (err) {
        logger.error(err)
      } finally {
        commit('CART_REQUEST_NOT_PENDING')
      }
    },
    async getCart({ commit }) {
      commit('CART_REQUEST_PENDING')
      try {
        const { data: cart } = await axios.get(getApiUrl('/api/v1/cart/get-cart'))
        commit('UPDATE_CART', cart)
        if (cart.lines.length === 1) {
          commit('UPDATE_ROOM_UPGRADES_AVAILABLE', cart.lines[0].upgradeRoom)
          commit('UPDATE_ROOM_UPGRADE_SELECTED', cart.lines[0].upgradeSelected)
          if (cart.lines[0].upgradeSelected) {
            if (cart.lines[0].previousCartItem) {
              commit('UPDATE_PREVIOUS_CART_ITEM', cart.lines[0].previousCartItem)
              commit('UPDATE_PREVIOUS_ROOM_UPGRADE_INFO', cart.lines[0].previousRoomUpgradeInfo)
              commit('UPDATE_PREVIOUS_ROOM_UPGRADE_IMAGE', cart.lines[0].previousRoomUpgradeImage)
              commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', true)
            }
          }
        }
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    async resetCart(
      { commit, dispatch }
    ) {
      commit('CART_REQUEST_PENDING')
      try {
        await axios.delete(getApiUrl('/api/v1/cart/delete-cart'))
        await dispatch('getCart')
      } catch (err) {
        logger.error(err)
      }
      commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', false)
      commit('CART_REQUEST_NOT_PENDING')
    },
    async addToCart(
      {
        commit,
        dispatch,
        rootGetters,
        rootState,
        getters
      },
      {
        item,
        skipValidateRoute = false
      }
    ) {
      commit('CART_REQUEST_PENDING')
      const params = getParams(item, rootGetters, rootState)
      const currentRoomId = rootGetters['booking/getCurrentRoomId']
      const allRoomsGuests = [{ adultGuestCount: params.adultGuestCount, childGuestCount: params.childGuestCount }, ...rootState.booking.query.aditionalGuests]
      const maxRoomGuests = item.maxGuests || 5
      const hasGuestsExceededLimit = allRoomsGuests.some((room) => (parseInt(room.adultGuestCount) + parseInt(room.childGuestCount)) > maxRoomGuests)
      let roomQuantity = Number(rootState.booking.query.roomQuantity)

      if (rootGetters['cart/itemsInCart'].length === 0
        && getters.getResolveMultiBookingSidebar === null
        && item?.numberOfUnits >= roomQuantity
        && roomQuantity > 1
        && !hasGuestsExceededLimit
        && getters.isMultiRoomBookingEnabled) {
        const multoroomPromise = new Promise((resolve) => {
          commit('RESOLVE_MULTIBOOKING_SIDEBAR', resolve)
          commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', false)
        })
        if (currentRoomId === 0) {
          commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', true)
          commit('UPDATE_MULTIBOOKING_SIDEBAR_DATA', item)
        }
        const userResolveMultiBooking = await multoroomPromise
        if (!userResolveMultiBooking) {
          roomQuantity = 1
          commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', false)
        }
      } else {
        roomQuantity = 1
      }
      commit('RESOLVE_MULTIBOOKING_SIDEBAR', null)
      commit('UPDATE_MULTIBOOKING_SIDEBAR_DATA', null)
      commit('UPDATE_ROOM_UPGRADES_AVAILABLE', params.upgradeRoom)
      commit('UPDATE_ROOM_UPGRADE_SELECTED', params.upgradeSelected)
      try {
        const dataToSend = {
          ...params,
          roomQuantity,
          roomsGuests: allRoomsGuests,
        }
        const { data: cart } = await axios.post(getApiUrl('/api/v1/cart/add-to-cart'), dataToSend)
        commit('UPDATE_CART', cart)
        if (!skipValidateRoute) {
          dispatch('booking/validateRoute', { isAllSameRooms: roomQuantity > 1 }, { root: true })
        }
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    async updateCartLine(
      {
        commit,
        dispatch,
        getters,
        rootGetters,
        rootState
      },
      item, isSelected = false
    ) {
      commit('CART_REQUEST_PENDING')
      const params = getParams(item, rootGetters, rootState)
      commit('UPDATE_ROOM_UPGRADES_AVAILABLE', params.upgradeRoom)
      const currentCartLineId = rootGetters['booking/currentRoom'].id
      const allCartLineIds = getters.cartLineIds
      let updateAllCartLinesWithSameData = false

      const currentRoomId = rootGetters['booking/getCurrentRoomId']
      const allRoomsGuests = [{ adultGuestCount: params.adultGuestCount, childGuestCount: params.childGuestCount }, ...rootState.booking.query.aditionalGuests]
      const maxRoomGuests = item.maxGuests || 5
      const hasGuestsExceededLimit = allRoomsGuests.some((room) => (parseInt(room.adultGuestCount) + parseInt(room.childGuestCount)) > maxRoomGuests)
      const roomQuantity = Number(rootState.booking.query.roomQuantity)

      if (rootState.modifyReservation.isBookingEditMode
        && getters.getResolveMultiBookingSidebar === null
        && item?.numberOfUnits >= roomQuantity
        && roomQuantity > 1
        && !hasGuestsExceededLimit
        && currentRoomId === 0) {
        const multiroomPromise = new Promise((resolve) => {
          commit('RESOLVE_MULTIBOOKING_SIDEBAR', resolve)
          commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', false)
        })
        commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', true)
        commit('UPDATE_MULTIBOOKING_SIDEBAR_DATA', item)
        const userResolveMultiBooking = await multiroomPromise
        if (userResolveMultiBooking && rootState.modifyReservation.isBookingEditMode) {
          updateAllCartLinesWithSameData = true
          commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', false)
        } else {
          updateAllCartLinesWithSameData = false
          commit('UPDATE_SHOW_MULTIBOOKING_SIDEBAR', false)
        }
      }

      commit('RESOLVE_MULTIBOOKING_SIDEBAR', null)
      commit('UPDATE_MULTIBOOKING_SIDEBAR_DATA', null)

      try {
        const apiUrl = updateAllCartLinesWithSameData ? '/api/v1/cart/update-all-cart-lines' : '/api/v1/cart/update-cart-line'

        const requestData = {
          ...params,
          ...(updateAllCartLinesWithSameData
            ? { cartLineIds: allCartLineIds }
            : { cartLineId: currentCartLineId }),
        }

        const { data: cart } = await axios.put(getApiUrl(apiUrl), requestData)

        commit('UPDATE_CART', cart)
        if (!isSelected) {
          await dispatch('clearEnhancementsFromRooms', currentCartLineId)
        }

        dispatch('booking/validateRoute', { isAllSameRooms: updateAllCartLinesWithSameData }, { root: true })
        return cart
      } catch (err) {
        logger.error(err)
      } finally {
        commit('CART_REQUEST_NOT_PENDING')
      }
    },
    async deleteFromCart(
      {
        commit,
        getters
      },
      cartLineIndex
    ) {
      commit('CART_REQUEST_PENDING')
      const params = {
        data: {
          CartLineId: getters.itemsInCart[cartLineIndex]?.id
        }
      }
      try {
        const { data: cart } = await axios.delete(getApiUrl('/api/v1/cart/delete-cart-line'), params)
        commit('UPDATE_CART', cart)
        commit('UPDATE_ROOM_UPGRADES_AVAILABLE', [])
        await dispatch()
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    async addEnhancementToAllRooms(
      {
        commit,
        getters,
        rootGetters
      }
    ) {
      commit('CART_REQUEST_PENDING')
      const cartLines = getters.itemsInCart
      const { enhancements } = getters.itemsInCart[0]
      const data = cartLines.map((item) => ({
        lineId: item.id,
        enhancements: [...enhancements] // если enhancements - общий массив для всех lineId
      }))
      try {
        const { data: cart } = await axios.put(getApiUrl('/api/v1/cart/add-enhancement'), data)
        commit('UPDATE_CART', cart)
      } catch (err) {
        logger.error(err)
      }
      commit('ENHANCEMENT_SELECTED_FOR_MULTIBOOKING', false)
      commit('CART_REQUEST_NOT_PENDING')
    },
    async addEnhancementToRoom(
      {
        commit,
        getters,
        rootGetters
      },
      enhancementArray
    ) {
      commit('CART_REQUEST_PENDING')
      const roomId = rootGetters['booking/currentRoom']?.id
      const addOns = [...getters.addOnsInRoom]
      const addOnsParams = [
        ...addOns
      ]
      const isMultipleUpdates = enhancementArray.length > 1
      // Get enhancements already added to room
      const enhancementsInTheRoom = [...getters.enhancementsInRoom]
      const newEnhancements = []
      enhancementArray.forEach((enhancementToAdd) => {
        const enhancementIndex = enhancementsInTheRoom.findIndex((e) => e.sitecoreId === enhancementToAdd.sitecoreId)
        if (enhancementIndex > -1) enhancementsInTheRoom.splice(enhancementIndex, 1)
      })
      // Get enhancement input methods
      const methods = !isMultipleUpdates ? getUserInputMethods(enhancementArray[0]?.userInput) : null

      // Merge enhancements in room with new enhancement
      const enhancementsParams = [
        ...enhancementsInTheRoom,
        ...enhancementArray
      ].map((e) => { // Check if mapped enhancement is the enhancement being added
        const isAddingEnhancement = enhancementArray.filter((enhancement) => e.sitecoreId === enhancement.sitecoreId).length > 0
        const additionalMethods = getUserInputMethods(e.userInput)
        // If enhancement input methods have a description use it, if not use the enhancement name
        const description = additionalMethods?.description ? additionalMethods.description(e) : e.name
        // If is the new enhancement use the generated description, if not use the enhancement description
        return {
          sitecoreId: e.sitecoreId,
          synXisId: e.synXisId,
          description: isAddingEnhancement ? description : e.description,
          data: e.data
        }
      })
      try {
        let { data: cart } = await axios.put(getApiUrl('/api/v1/cart/add-enhancement'), [{
          lineId: roomId,
          enhancements: enhancementsParams,
          addOns: addOnsParams
        }])
        // If the enhancements input methods export an onAdd method we will execute it
        if (!isMultipleUpdates && methods && typeof methods.onAdd === 'function') {
          cart = await methods.onAdd(enhancementArray[0], cart.lines.find((l) => l.id === roomId), rootGetters['booking/selectedHotel'])
        }
        commit('UPDATE_CART', cart)
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    async addAddOnToRoom(
      {
        commit,
        getters,
        rootGetters
      },
      { addOn }
    ) {
      commit('CART_REQUEST_PENDING')
      const roomId = rootGetters['booking/currentRoom']?.id

      const enhancements = [...getters.enhancementsInRoom]
      const enhancementsParams = [
        ...enhancements
      ]
      const addOns = [...getters.addOnsInRoom].filter((e) => e.sitecoreId !== addOn.sitecoreId)
      const addOnsParams = [
        ...addOns,
        addOn
      ].map((e) => ({
        code: e.synXisId,
        sitecoreId: e.sitecoreId,
        synXisId: e.synXisId,
        addOnName: e.addOnName,
        quantity: e.quantity,
        adultQuantity: e.adultQuantity,
        childQuantity: e.childQuantity,
        price: e.price,
        priceWithTaxes: e.priceWithTaxes,
        total: e.total,
        totalTax: e.totalTax,
        addOnTax: e.addOnTax,
        servicePricingType: e.servicePricingType,
        data: e.data,
        airportAddOn: e.airportAddOn,
        excludeKidsFromPriceCalculations: e.excludeKidsFromPriceCalculations,
      }))
      try {
        const { data: cart } = await axios.put(getApiUrl('/api/v1/cart/add-enhancement'), [{
          lineId: roomId,
          enhancements: enhancementsParams,
          addOns: addOnsParams
        }])
        commit('UPDATE_CART', cart)
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    async removeEnhancementFromRoom(
      {
        commit,
        getters,
        state,
        rootGetters
      },
      { enhancementId }
    ) {
      commit('CART_REQUEST_PENDING')
      const roomId = rootGetters['booking/currentRoom']?.id
      const addOns = [...getters.addOnsInRoom]
      const addOnsParams = [
        ...addOns
      ]
      const enhancement = state.roomEnhancements.find((e) => e.sitecoreId === enhancementId)
      // Remove the enhancement from the room
      const enhancements = [...getters.enhancementsInRoom].filter((e) => e.sitecoreId !== enhancementId)
      // Get enhancement input methods
      const methods = getUserInputMethods(enhancement.userInput)
      try {
        let { data: cart } = await axios.put(getApiUrl('/api/v1/cart/add-enhancement'), [{
          lineId: roomId,
          enhancements,
          addOns: addOnsParams
        }])
        // If the enhancements input methods export an onRemove method we will execute it
        if (methods && typeof methods.onRemove === 'function') {
          cart = await methods.onRemove(enhancement, cart.lines.find((l) => l.id === roomId), rootGetters['booking/selectedHotel'])
        }
        commit('UPDATE_CART', cart)
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    async removeAddOnFromRoom(
      {
        commit,
        getters,
        state,
        rootGetters
      },
      { addOn }
    ) {
      commit('CART_REQUEST_PENDING')
      const roomId = rootGetters['booking/currentRoom']?.id
      const enhancementsInRoom = [...getters.enhancementsInRoom]
      const enhancementsParams = [
        ...enhancementsInRoom
      ]
      const addOns = [...getters.addOnsInRoom].filter((e) => e.sitecoreId !== addOn.sitecoreId)

      try {
        const { data: cart } = await axios.put(getApiUrl('/api/v1/cart/add-enhancement'), [{
          lineId: roomId,
          enhancements: enhancementsParams,
          addOns
        }])
        commit('UPDATE_CART', cart)
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    },
    resetRoomUpgrades({ commit }) {
      logger.debug('reset room upgrades')
      commit('UPDATE_ROOM_UPGRADE_SELECTED', false)
      commit('UPDATE_ROOM_UPGRADES_AVAILABLE', [])
      commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', false)
      commit('UPDATE_ROOM_UPGRADE_AVAILABLE', null)
      localStorage.removeItem('oldBedTypeCode')
    },
    async getRoomUpgradesAvailability(
      {
        state,
        commit,
        rootState,
        rootGetters,
        dispatch,
        getters
      }
    ) {
      const guests = rootGetters['booking/currentRoomGuests']
      const itemInCart = rootGetters['cart/itemsInCart'][0]
      const roomUpgradeOptions = rootGetters['cart/getRoomUpgradesOptions']
      const bookingQuery = rootState.booking.query
      const roomCodes = []

      roomUpgradeOptions.map((upgrade) => {
        roomCodes.push(upgrade.bedTypeSynXisId)
      })

      const query = {
        adultGuestCount: bookingQuery.adultGuestCount,
        childGuestCount: bookingQuery.childGuestCount,
        hotelCode: bookingQuery.hotelCode,
        roomQuantity: bookingQuery.roomQuantity,
        rateCode: itemInCart.ratePlanCode,
        roomCodes,
        stayDateEnd: bookingQuery.stayDateEnd,
        stayDateStart: bookingQuery.stayDateStart,
        primaryLanguageId: rootState?.global?.lang ?? 'en'
      }
      commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', true)
      commit('LOADING_ROOM_UPGRADES', true)
      try {
        const { data } = await axios.post(getApiUrl('/api/v1/booking/check-room-availability'), query)
        let roomUpgrade = null
        if (data.roomStays && data.countStays > 0) {
          roomCodes.some((code) => {
            const matchingBed = data.roomStays
              .flatMap((stay) => stay.bedType)
              .find((bed) => bed.synXisID.trim() === code.toString().trim())

            if (matchingBed) {
              roomUpgrade = data.roomStays.find((stay) => stay.bedType.includes(matchingBed))
              return true
            }

            return false
          })
        }
        if (roomUpgrade !== null) {
          commit('UPDATE_ROOM_UPGRADE_AVAILABLE', roomUpgrade)
          commit('UPDATE_ROOM_UPGRADE_TITLE', roomUpgrade.title)
          commit('UPDATE_ROOM_UPGRADE_TOTAL', state.roomUpgradeAvailable.rates[0].total)
          commit('UPDATE_ROOM_UPGRADE_PRICE_DIFFERENCE', getters.getRoomUpgradePriceDifferencePerNight)
          commit('UPDATE_PREVIOUS_CART_ITEM', itemInCart)
          commit('UPDATE_PREVIOUS_ROOM_UPGRADE_INFO', getters.getRoomUpgradeAvailableInfo)
          commit('UPDATE_PREVIOUS_ROOM_UPGRADE_IMAGE', roomUpgrade?.images[0].src)
          logger.debug('get upgrade available')
        } else if (itemInCart.upgradeSelected) {
          commit('UPDATE_ROOM_UPGRADE_TITLE', itemInCart.sitecoreContent.room.title)
          commit('UPDATE_ROOM_UPGRADE_TOTAL', itemInCart.total)
          commit('UPDATE_ROOM_UPGRADE_PRICE_DIFFERENCE', getters.getRoomUpgradePriceDifferencePerNight)
          commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', true)
        } else {
          commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', false)
          commit('UPDATE_ROOM_UPGRADE_AVAILABLE', null)
        }
        return { roomUpgrade }
      } catch (err) {
        logger.error(err)
        if (err.response.status === 400) {
          commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', false)
          commit('UPDATE_ROOM_UPGRADE_AVAILABLE', null)
        }
      } finally {
        commit('LOADING_ROOM_UPGRADES', false)
      }
    },
    async applyRoomUpgrade({
      state, commit, dispatch, getters, rootState
    }) {
      commit('LOADING_ROOM_UPGRADES', true)
      localStorage.setItem('oldBedTypeCode', getters.itemsInCart[0].bedTypeCode)
      const upgradeRoomCartItem = {
        ...rootState['booking/query'],
        roomTypeCode: state.roomUpgradeAvailable.synXisID,
        ratePlanCode: state.roomUpgradeAvailable.rates[0].synXisID,
        bedTypeCode: state.roomUpgradeAvailable.bedType[0].synXisID,
        fees: state.roomUpgradeAvailable.rates[0].fees,
        taxes: state.roomUpgradeAvailable.rates[0].taxes,
        total: state.roomUpgradeAvailable.rates[0].total,
        taxesFees: state.roomUpgradeAvailable.rates[0].taxesFees,
        nightlyRates: state.roomUpgradeAvailable.rates[0].nightlyRates,
        sitecoreContent: {
          room: {
            title: state.roomUpgradeAvailable.title,
            description: state.roomUpgradeAvailable.shortDescription,
            image: state.roomUpgradeAvailable.images[0].src
          },
          rate: {
            title: state.roomUpgradeAvailable.rates[0].title,
            description: state.roomUpgradeAvailable.rates[0].description
          },
          bed: {
            title: state.roomUpgradeAvailable.bedType[0].title,
            description: state.roomUpgradeAvailable.bedType[0].description
          }
        },
        restrictionPolicy: state.roomUpgradeAvailable.rates[0].restrictionPolicy,
        upgradeRoom: [],
        upgradeSelected: true,
        upgradePriceDifferencePerNight: getters.getRoomUpgradePriceDifferencePerNight,
        previousCartItem: getters.itemsInCart[0],
        previousRoomUpgradeInfo: getters.getRoomUpgradeAvailableInfo,
        previousRoomUpgradeImage: getters.getRoomUpgradeAvailable.images[0].src,
      }
      try {
        dispatch('updateCartLine', upgradeRoomCartItem).then((data) => {
          commit('UPDATE_PREVIOUS_CART_ITEM', data.lines[0].previousCartItem)
          commit('UPDATE_PREVIOUS_ROOM_UPGRADE_INFO', data.lines[0].previousRoomUpgradeInfo)
          commit('UPDATE_PREVIOUS_ROOM_UPGRADE_IMAGE', data.lines[0].previousRoomUpgradeImage)

          commit('UPDATE_ROOM_UPGRADE_SELECTED', true)
          commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', true)
          commit('UPDATE_ROOM_UPGRADES_AVAILABLE', [])
          commit('UPDATE_ROOM_UPGRADE_AVAILABLE', null)
          commit('LOADING_ROOM_UPGRADES', false)

          commit('rooms/UPDATE_SELECTED_BED_TYPES', [], { root: true })
          commit('rooms/UPDATE_SELECTED_HIGHLIGHTS', [], { root: true })
          return data
        })
      } catch (error) {
        commit('LOADING_ROOM_UPGRADES', false)
        logger.error(error)
      }
    },
    async removeRoomUpgrade({
      state, commit, dispatch, getters, rootState
    }) {
      commit('LOADING_ROOM_UPGRADES', true)
      const previousCartItem = {
        ...rootState['booking/query'],
        roomTypeCode: getters.itemsInCart[0].previousCartItem.roomTypeCode,
        ratePlanCode: getters.itemsInCart[0].previousCartItem.ratePlanCode,
        bedTypeCode: getters.itemsInCart[0].previousCartItem.bedTypeCode,
        fees: getters.itemsInCart[0].previousCartItem.fee,
        taxes: getters.itemsInCart[0].previousCartItem.taxes,
        total: getters.itemsInCart[0].previousCartItem.total,
        taxesFees: getters.itemsInCart[0].previousCartItem.taxesFees,
        nightlyRates: getters.itemsInCart[0].previousCartItem.nightlyRates,
        sitecoreContent: {
          room: {
            title: getters.itemsInCart[0].previousCartItem.sitecoreContent.room.title,
            description: getters.itemsInCart[0].previousCartItem.sitecoreContent.room.shortDescription,
            image: getters.itemsInCart[0].previousCartItem.sitecoreContent.room.image
          },
          rate: {
            title: getters.itemsInCart[0].previousCartItem.sitecoreContent.rate.title,
            description: getters.itemsInCart[0].previousCartItem.sitecoreContent.rate.description
          },
          bed: {
            title: getters.itemsInCart[0].previousCartItem.sitecoreContent.bed.title,
            description: getters.itemsInCart[0].previousCartItem.sitecoreContent.bed.description
          }
        },
        restrictionPolicy: getters.itemsInCart[0].previousCartItem.restrictionPolicy,
        upgradeRoom: getters.itemsInCart[0].previousCartItem.upgradeRoom,
        upgradeSelected: false,
        upgradePriceDifferencePerNight: getters.itemsInCart[0].previousCartItem.upgradePriceDifferencePerNight,
        previousCartItem: {}
      }
      try {
        dispatch('updateCartLine', previousCartItem).then((data) => {
          commit('UPDATE_ROOM_UPGRADES_AVAILABLE', data.lines[0].upgradeRoom)
          commit('UPDATE_ROOM_UPGRADE_SELECTED', false)
          commit('UPDATE_SHOW_ROOM_UPGRADE_BUTTON', true)
          commit('UPDATE_ROOM_UPGRADE_AVAILABLE', null)
          return data
        })
      } catch (error) {
        commit('LOADING_ROOM_UPGRADES', false)
        logger.error(error)
      }
    },
    async clearEnhancementsFromRooms({
      commit,
      rootGetters
    }, currentRoomId) {
      commit('CART_REQUEST_PENDING')
      const roomId = currentRoomId || rootGetters['booking/currentRoom']?.id
      // Remove all enhancements
      const enhancements = null
      try {
        const { data: cart } = await axios.put(getApiUrl('/api/v1/cart/add-enhancement'), [{
          lineId: roomId,
          enhancements
        }])
        commit('UPDATE_CART', cart)
      } catch (err) {
        logger.error(err)
      }
      commit('CART_REQUEST_NOT_PENDING')
    }
  }
}
